import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { MapPin, CalendarDays } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRESET_DAYS = [
  { label: '7d', value: 7 },
  { label: '14d', value: 14 },
  { label: '30d', value: 30 },
  { label: '60d', value: 60 },
  { label: '90d', value: 90 },
];

const _iso = (d) => d.toISOString().slice(0, 10);
// Today / Yesterday are expressed as single-day custom ranges — the
// backend's _date_match already understands 'YYYY-MM-DD:YYYY-MM-DD'.
export const todayRange = () => { const t = _iso(new Date()); return `${t}:${t}`; };
export const yesterdayRange = () => { const y = _iso(new Date(Date.now() - 86400000)); return `${y}:${y}`; };

const pillCls = (active) =>
  `px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
    active
      ? 'bg-primary text-white border-primary'
      : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
  }`;

export const isCustomRange = (days) => typeof days === 'string' && days.includes(':');

export const formatDaysLabel = (days) => {
  if (days === todayRange()) return 'today';
  if (days === yesterdayRange()) return 'yesterday';
  return isCustomRange(days)
    ? days.replace(':', ' → ')
    : days ? `last ${days} days` : 'all time';
};

export const DateRangePills = ({ days, onChange, includeAll = true }) => {
  const tVal = todayRange();
  const yVal = yesterdayRange();
  const isToday = days === tVal;
  const isYesterday = days === yVal;
  const custom = isCustomRange(days) && !isToday && !isYesterday;
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(custom ? days.split(':')[0] : '');
  const [to, setTo] = useState(custom ? days.split(':')[1] : '');
  const presets = [
    { label: 'Today', value: tVal },
    { label: 'Yesterday', value: yVal },
    ...(includeAll ? [{ label: 'All', value: null }] : []),
    ...PRESET_DAYS,
  ];
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="date-range-pills">
      <CalendarDays className="w-3.5 h-3.5 text-slate-400 mr-0.5" />
      {presets.map(p => (
        <button
          key={p.label}
          onClick={() => { setOpen(false); onChange(p.value); }}
          className={pillCls(days === p.value)}
          data-testid={`date-range-pill-${p.label}`}
        >
          {p.label}
        </button>
      ))}
      <button
        onClick={() => setOpen(o => !o)}
        className={pillCls(custom)}
        data-testid="date-range-pill-custom"
      >
        {custom ? days.replace(':', ' → ') : 'Custom'}
      </button>
      {open && (
        <span className="flex items-center gap-1" data-testid="custom-range-picker">
          <input
            type="date" value={from} onChange={(e) => setFrom(e.target.value)}
            className="text-xs border border-slate-200 rounded-md px-1.5 py-1"
            data-testid="custom-range-from"
          />
          <span className="text-xs text-slate-400">–</span>
          <input
            type="date" value={to} onChange={(e) => setTo(e.target.value)}
            className="text-xs border border-slate-200 rounded-md px-1.5 py-1"
            data-testid="custom-range-to"
          />
          <button
            disabled={!from || !to || from > to}
            onClick={() => { onChange(`${from}:${to}`); setOpen(false); }}
            className="text-xs font-semibold px-2 py-1 rounded-md bg-primary text-white disabled:opacity-40"
            data-testid="custom-range-apply"
          >
            Apply
          </button>
        </span>
      )}
    </div>
  );
};

export const SiteFilterPills = ({ domainId, site, onChange }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    const qs = domainId ? `?domain_id=${domainId}` : '';
    axios.get(`${API}/analytics/sites${qs}`)
      .then(r => {
        setData(r.data);
        // Single-site scoped user: lock the filter to their one site.
        if (r.data?.restricted && r.data.sites.length === 1) onChange(r.data.sites[0]);
      })
      .catch(() => setData({ sites: [], restricted: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainId]);

  if (!data || data.sites.length === 0) return null;
  const showAll = !data.restricted || data.sites.length > 1;
  const lockSingle = data.restricted && data.sites.length === 1;
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="site-filter-pills">
      <MapPin className="w-3.5 h-3.5 text-slate-400 mr-0.5" />
      {showAll && (
        <button
          onClick={() => onChange(null)}
          className={pillCls(!site)}
          data-testid="site-pill-all"
        >
          {data.restricted ? 'My sites' : 'All'}
        </button>
      )}
      {data.sites.map(s => (
        <button
          key={s}
          onClick={() => { if (!lockSingle) onChange(s); }}
          className={pillCls(site === s || (lockSingle && true))}
          data-testid={`site-pill-${s}`}
        >
          {s}
        </button>
      ))}
    </div>
  );
};

export const AnalyticsFilters = ({ domainId, days, site, onDaysChange, onSiteChange }) => (
  <div className="flex items-center justify-between gap-4 flex-wrap bg-white border border-slate-200 rounded-xl px-4 py-2.5 mb-4" data-testid="analytics-filters">
    <DateRangePills days={days} onChange={onDaysChange} />
    <SiteFilterPills domainId={domainId} site={site} onChange={onSiteChange} />
  </div>
);
